import * as moment from 'moment'
import { EARLIEST_DATA_DATE } from 'constants/datetime'
import { Car, Home, HomeFormValues, RawCarFormValues, CarFormValues } from 'interfaces/property'
import { cityToOption } from 'helpers/cityHelpers'
import { Action } from 'actions'
import { CAR_DELETED, CAR_UNLINKED, HOME_DELETED, HOME_UNLINKED } from 'actions/propertyActions'
import { FORM_HOMES_EDIT, FORM_CARS_EDIT } from 'constants/forms'
import { isFormSubmittedAction } from 'helpers/actionsHelpers'

export const getHomePriceKey = ({ address1, address2, city, zip5 }: HomeFormValues) => {
  if (!(address1 && city && zip5)) {
    return null
  }
  // generate key for application state prices cache as JSON string because it's simplier than hashing
  return JSON.stringify([address1, address2 || '', city.value, zip5])
}

export const getPurchaseDateOptions = () => {
  const firstOption = moment(EARLIEST_DATA_DATE).subtract(1, 'month')
  const purchaseDateOptions = [{
    label: firstOption.format('MMM Y') + ' or Earlier',
    value: firstOption.format('YYYY-MM'),
  }]

  const startDate = moment(EARLIEST_DATA_DATE)
  const endDate = moment()
  while (endDate > startDate) {
    purchaseDateOptions.push({
      label: startDate.format('MMM Y'),
      value: startDate.format('YYYY-MM'),
    })
    startDate.add(1, 'month')
  }

  purchaseDateOptions.reverse()

  return purchaseDateOptions
}

export const homeToFormValues = (home: Home): HomeFormValues => ({
  address1: home.address1,
  address2: home.address2 || '',
  city: cityToOption(home.city),
  estimated_value: home.estimated_value ? home.estimated_value : null,
  zip5: home.zip5,
  purchased_at: home.purchased_at,
  is_primary_residence: Boolean(home.is_primary_residence),
})

export const canSendHomePriceRequest = (homeAsyncErrors: Partial<Record<keyof Home, string>> | null) =>
  !homeAsyncErrors // TODO: this is a crutch because I can't reproduce https://sentry.io/tangle-inc-dw/prodfront/issues/410569615/
  || Object.keys(homeAsyncErrors).every((error) => ['estimated_value', 'purchased_at'].includes(error))

export const carToRawFormValues = (car: Car): RawCarFormValues => {
  const make = car.make.id
  const model = car.model.id

  return { ...car, make, model }
}

export const carToFormValues = (car: Car): CarFormValues => ({
  make: car.make.id,
  model: car.model.id,
  year: car.year,
  estimated_value: car.estimated_value ? car.estimated_value : null,
  purchased_at: car.purchased_at,
})

export const doesChangeProperty = (action: Action): boolean => {
  const propertyFormSubmitted = isFormSubmittedAction(FORM_HOMES_EDIT, FORM_CARS_EDIT)(action)

  const propertyDeleted = action.type === HOME_DELETED || action.type === CAR_DELETED
  const propertyUnlinked = action.type === HOME_UNLINKED || action.type === CAR_UNLINKED

  return propertyFormSubmitted || propertyDeleted || propertyUnlinked
}
